import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface BenchmarkingCriteriaProps {
  className?: string;
}

interface CriteriaItem {
  readonly category: string;
  readonly criteria: string;
  readonly description: string;
  readonly highlighted?: boolean;
}

interface Pillar {
  readonly name: string;
  readonly color: string;
  readonly items: readonly CriteriaItem[];
}

// Static benchmarking framework data - exported as immutable constant for reuse
export const benchmarkingPillars = [
  {
    name: 'Strategic Health',
    color: 'bg-blue-100 text-blue-800',
    items: [
      {
        category: 'Strategic Clarity',
        criteria: 'Long-term strategies',
        description: 'Long term strategies precisely defined, and clear financial and consulting models'
      },
      {
        category: 'Financial Strength',
        criteria: 'Margins & Growth',
        description: 'High gross and net margins, revenue growth, high revenue sold/worked per FTE',
        highlighted: true
      },
      {
        category: 'Portfolio Strength',
        criteria: 'Client Mix',
        description: 'Balanced new vs. repeat, long tenure clients, low concentration, high backlog',
        highlighted: true
      },
      {
        category: 'Impact',
        criteria: 'Value Proposition',
        description: 'Clearly defined target, narrow problem solved that is urgent, expensive, pervasive; strong, consistent Value Proposition'
      }
    ]
  },
  {
    name: 'Winning Engagements',
    color: 'bg-green-100 text-green-800',
    items: [
      {
        category: 'Biz Dev Mindset',
        criteria: 'BD Investment',
        description: 'Appropriate time spent on BD and marketing'
      },
      {
        category: 'Visibility',
        criteria: 'Marketing Presence',
        description: 'Strength, consistency and breadth of efforts on the 5 Marketing Musts',
        highlighted: true
      },
      {
        category: 'Connecting',
        criteria: 'Network Development',
        description: 'Size and growth of Network Core; relationship building, nurturing and planning effectiveness'
      },
      {
        category: 'Obvious Choice',
        criteria: 'Discovery Process',
        description: 'Strong, Right-Side Up discovery process; including fee-tolerance and reframing'
      },
      {
        category: 'Propose, Negotiate & Close',
        criteria: 'Sales Excellence',
        description: 'Excellent, Right-Side Up proposals, with high-margin fee structures; negotiation best practices'
      }
    ]
  },
  {
    name: 'Creating Value',
    color: 'bg-purple-100 text-purple-800',
    items: [
      {
        category: 'Quality Delivery',
        criteria: 'Client Satisfaction',
        description: 'Capability and track record for creating exceptional client satisfaction'
      },
      {
        category: 'Leverage',
        criteria: 'Innate Genius Utilization',
        description: 'Extent to which everyone in the firm is working against their Innate Genius; high utilization',
        highlighted: true
      },
      {
        category: 'Scalability',
        criteria: 'Systemization',
        description: 'Standardization and systemization of approaches across all aspects of the firm'
      },
      {
        category: 'Client Experience',
        criteria: 'Experience Design',
        description: 'Processes and practices in place to create exceptional client experience'
      }
    ]
  },
  {
    name: 'Infrastructure',
    color: 'bg-orange-100 text-orange-800',
    items: [
      {
        category: 'Organizational Health',
        criteria: 'Talent Management',
        description: 'Attrition at various levels, recruitment and onboarding processes'
      },
      {
        category: 'Support Strength',
        criteria: 'Operations Excellence',
        description: 'Distribution of firm responsibilities, appropriate non-consulting systems and personnel'
      },
      {
        category: 'Organization Management',
        criteria: 'Management Practices',
        description: 'Role clarity and consistency, career pathing and management practices, capacity planning',
        highlighted: true
      },
      {
        category: 'Partner Management',
        criteria: 'Partnership Growth',
        description: 'Partner evaluation, management, growth practices'
      },
      {
        category: 'Board Quality',
        criteria: 'Advisory Excellence',
        description: 'Existence, quality and focus of advisory board'
      },
      {
        category: 'Leadership Quality',
        criteria: 'Vision & Culture',
        description: 'Conception, communication and implementation of vision, strategy and culture'
      }
    ]
  }
];

export function BenchmarkingCriteria({ className = '' }: BenchmarkingCriteriaProps) {
  return (
    <div className={`space-y-6 ${className}`}>
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-2">DAFCG Benchmarking Criteria</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Four main pillars that assess boutique consulting firm performance and health across 100 participating firms.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
        {benchmarkingPillars.map((pillar, pillarIndex) => (
          <Card key={pillarIndex} className="h-fit">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Badge className={pillar.color} variant="secondary">
                  {pillar.name}
                </Badge>
              </div>
              <CardDescription>
                {pillar.items.length} criteria categories
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pillar.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="border-l-2 border-gray-200 pl-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm">{item.category}</h4>
                        <p className="text-sm text-gray-600 font-medium">{item.criteria}</p>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                      {item.highlighted && (
                        <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
                          Key
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Scoring Methodology</CardTitle>
          <CardDescription>
            How the benchmarking assessment works
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-sm mb-2">Percentile Rankings</h4>
              <p className="text-xs text-gray-600">Where each firm stands relative to peers</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-sm mb-2">Category Scores</h4>
              <p className="text-xs text-gray-600">Performance within each pillar</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-sm mb-2">Overall Health Score</h4>
              <p className="text-xs text-gray-600">Composite score across all four pillars</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-sm mb-2">Maturity Assessment</h4>
              <p className="text-xs text-gray-600">Growth stage identification</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}